import type React from "react";
import {useEffect, useMemo, useState} from "react";
import {useAuthState} from "react-firebase-hooks/auth";

import {useUpdateUser, useUser, useUserRealtimeSub} from "~/api/hooks/useUser";
import CustomButton from "~/component/common/CustomButton";
import {auth} from "~/firebase/firebase";

interface ProfileContentProps {
  closeModal: () => void;
}

const ProfileContent: React.FC<ProfileContentProps> = () => {
  const [user] = useAuthState(auth);
  const {mutate: updateUser} = useUpdateUser();
  const userKey = useMemo(() => ({id: user?.uid ?? "unknown"}), [user?.uid]);
  const {data, isLoading: isLoadingData} = useUser(userKey);
  useUserRealtimeSub(userKey);

  const [name, setName] = useState("");
  const [color, setColor] = useState("");

  useEffect(() => {
    if (data) {
      setName(data.name);
      setColor(data.color);
    } else {
      setName("");
      setColor("#ffffff");
    }
  }, [data]);

  const hasChanges = useMemo(() => {
    if (!data) return false;
    return name !== data.name || color !== data.color;
  }, [data, name, color]);

  const handleSave = () => {
    if (!user || !hasChanges) return;
    updateUser({
      key: {id: user.uid},
      data: {id: user.uid, name, color},
    });
  };

  return (
    <div className="w-full text-white">
      <div className="space-y-4">
        <div className="mb-4">
          <label
            htmlFor="username"
            className="block text-gray-300 text-sm mb-2"
          >
            Username
          </label>
          <input
            type="text"
            id="username"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full p-2 bg-gray-700 bg-opacity-50 border rounded-lg focus:outline-none border-gray-500 focus:border-blue-500"
          />
        </div>

        <div className="mb-4">
          <label htmlFor="color" className="block text-gray-300 text-sm mb-2">
            Favorite Color
          </label>
          <input
            type="color"
            id="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-full h-12 cursor-pointer bg-gray-700 bg-opacity-50 border rounded-lg border-gray-500"
            style={{backgroundColor: color}}
          />
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <CustomButton
          onClick={handleSave}
          disabled={isLoadingData || !hasChanges}
          className="bg-blue-600/90 hover:bg-blue-500/90 text-white py-2 px-4 rounded-lg transition-colors disabled:bg-gray-600/90 disabled:cursor-not-allowed"
        >
          Apply
        </CustomButton>
      </div>
    </div>
  );
};

export default ProfileContent;
